import { useEffect, useRef, useState } from "react";
import { TESTIMONIALS } from "@/data/testimonials";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

const ROW1 = TESTIMONIALS.slice(0, 25);
const ROW2 = TESTIMONIALS.slice(25, 50);

/* ── Animated Counter ── */
function AnimatedCounter({ target, suffix = "", decimals = 0 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const duration = 2400;
        const startTime = performance.now();
        const step = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const val = eased * target;
          setCount(decimals ? parseFloat(val.toFixed(decimals)) : Math.round(val));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, decimals]);

  return (
    <span ref={ref}>
      {decimals ? count.toFixed(decimals) : count.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
}

/* ── Clip Reveal ── */
function ClipReveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      const t = setTimeout(() => el.classList.add("revealed"), delay * 1000);
      return () => clearTimeout(t);
    }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("revealed"); obs.disconnect(); }
    }, { threshold: 0.01, rootMargin: "0px 0px 60px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className="clip-reveal" style={{ transitionDelay: `${delay}s`, ...style }}>
      {children}
    </div>
  );
}

/* ── Testimonial Card ── */
function TestimonialCard({ t }) {
  return (
    <div style={{
      flexShrink: 0, width: "340px",
      border: `1px solid ${BORDER}`, padding: "28px 32px",
      backgroundColor: OFF_WHITE, display: "flex", flexDirection: "column",
      gap: "16px", marginRight: "20px",
    }}>
      <div style={{ display: "flex", gap: "3px" }}>
        {[1,2,3,4,5].map((s) => <span key={s} style={{ color: GOLD, fontSize: "0.75rem" }}>★</span>)}
      </div>
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: "#4A4A4A", lineHeight: 1.7, fontStyle: "italic", flex: 1 }}>
        "{t.quote}"
      </p>
      <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: "14px" }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem", fontWeight: 600, color: CHARCOAL }}>{t.name}</p>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.72rem", color: GOLD, marginTop: "3px", fontWeight: 500 }}>{t.role}</p>
      </div>
    </div>
  );
}

/* ── Marquee Row ── */
function MarqueeRow({ items, reverse }) {
  const trackRef = useRef(null);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let pos = reverse ? -(track.scrollWidth / 2) : 0;
    let raf;
    const speed = 0.38;
    const step = () => {
      pos += reverse ? speed : -speed;
      const half = track.scrollWidth / 2;
      if (!reverse && pos <= -half) pos = 0;
      if (reverse && pos >= 0) pos = -half;
      track.style.transform = `translateX(${pos}px)`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [reverse]);

  return (
    <div style={{ overflow: "hidden", width: "100%" }}>
      <div ref={trackRef} style={{ display: "flex", willChange: "transform" }}>
        {[...items, ...items].map((t, i) => <TestimonialCard key={`${t.id}-${i}`} t={t} />)}
      </div>
    </div>
  );
}

/* ── Main Section ── */
export default function TestimonialsSection() {
  return (
    <section data-testid="testimonials-section" style={{ padding: "96px 0", backgroundColor: "#F5F3EB", overflow: "hidden" }}>
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16" style={{ marginBottom: "64px" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-end">
          <div>
            <ClipReveal delay={0}>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>
                Client Testimonials
              </p>
            </ClipReveal>
            <ClipReveal delay={0.1}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, color: CHARCOAL, lineHeight: 1.15 }}>
                Trusted by Clients<br />Across the Globe
              </h2>
            </ClipReveal>
          </div>
          <div>
            <ClipReveal delay={0.2}>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#4A4A4A", lineHeight: 1.75, marginBottom: "28px" }} className="!text-[#4A4A4A]">
                From industrialists and celebrities to AI engineers and fintech founders — clients from 120+ countries trust Akkshay Narula for life-changing guidance.
              </p>
            </ClipReveal>

            {/* Animated Stats */}
            <div style={{ display: "flex", gap: "40px", flexWrap: "wrap" }}>
              <div>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.2rem", fontWeight: 500, color: GOLD, lineHeight: 1 }}>
                  <AnimatedCounter target={120} suffix="+" />
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#8A8A8A", marginTop: "5px" }}>
                  Countries Reached
                </p>
              </div>
              <div>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.2rem", fontWeight: 500, color: GOLD, lineHeight: 1 }}>
                  <AnimatedCounter target={24908} suffix="+" />
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#8A8A8A", marginTop: "5px" }}>
                  Consultations
                </p>
              </div>
              <div>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.2rem", fontWeight: 500, color: GOLD, lineHeight: 1 }}>
                  <span style={{ fontSize: "1.4rem", marginRight: "4px" }}>★</span>
                  <AnimatedCounter target={5.0} suffix="" decimals={1} />
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#8A8A8A", marginTop: "5px" }}>
                  Client Rating
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "20px" }}><MarqueeRow items={ROW1} reverse={false} /></div>
      <MarqueeRow items={ROW2} reverse={true} />
    </section>
  );
}
